import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Star, Crown, Sparkles } from "lucide-react";
import { PACKAGES } from "../lib/site-data";

const TIERS = [
  { id: "standard", label: "Standard", from: 50 },
  { id: "premium", label: "Premium", from: 125, icon: Crown },
];

export default function Pricing() {
  const navigate = useNavigate();
  const [tier, setTier] = useState("standard");
  const visible = PACKAGES.filter((p) => p.tier === tier);
  const isPremiumTier = tier === "premium";

  // Allow any page to deep-link into a specific package via:
  //  (a) URL hash — `#pricing-standard-cv` / `#pricing-premium-cv`
  //  (b) window event — `window.dispatchEvent(new CustomEvent('pricing:goto', {detail:{id:'standard-cv'}}))`
  // In both cases we flip the tier toggle to match the requested package and
  // scroll the card into view.
  useEffect(() => {
    const gotoPkg = (pkgId) => {
      const pkg = PACKAGES.find((p) => p.id === pkgId);
      if (!pkg) return;
      setTier(pkg.tier);
      window.setTimeout(() => {
        const el = document.querySelector(
          `[data-testid=pricing-card-${pkgId}]`,
        );
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 60);
    };
    const applyHash = () => {
      const match = (window.location.hash || "").match(/^#pricing-(.+)$/);
      if (match) gotoPkg(match[1]);
    };
    const onGoto = (e) => {
      const id = e?.detail?.id;
      if (id) gotoPkg(id);
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    window.addEventListener("pricing:goto", onGoto);
    return () => {
      window.removeEventListener("hashchange", applyHash);
      window.removeEventListener("pricing:goto", onGoto);
    };
  }, []);

  return (
    <section
      id="packages"
      data-testid="pricing-section"
      className="py-24 md:py-32 bg-background"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <p className="label-eyebrow">Packages</p>
          <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3 leading-tight">
            Simple pricing. Real results.
          </h2>
          <p className="mt-4 text-foreground/70">
            Choose <strong>Standard</strong> for a polished CV — or step up to{" "}
            <strong>Premium</strong> with a 1-on-1 career consultation call.
          </p>
        </div>

        {/* Tier toggle */}
        <div className="flex flex-col items-center gap-3 mb-10">
          <div
            data-testid="pricing-tier-toggle"
            className="relative inline-flex items-center bg-muted/60 rounded-full p-1 border border-border"
            role="tablist"
          >
            {TIERS.map((t) => {
              const active = t.id === tier;
              const isPremium = t.id === "premium";
              const inactivePremium = isPremium && !active;
              return (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={active}
                  data-testid={`pricing-tier-${t.id}`}
                  onClick={() => setTier(t.id)}
                  className={`relative flex items-center gap-2 rounded-full px-5 md:px-7 py-2.5 text-sm font-semibold transition ${
                    active
                      ? "bg-foreground text-background shadow"
                      : inactivePremium
                        ? "text-brand-600 ring-2 ring-brand/70 hover:bg-brand hover:text-white brand-pulse"
                        : "text-foreground/70 hover:text-foreground"
                  }`}
                >
                  {t.icon ? (
                    <t.icon
                      className={`h-4 w-4 ${
                        inactivePremium ? "text-brand-600" : "text-brand"
                      }`}
                    />
                  ) : null}
                  {t.label}
                  <span
                    className={`text-[10px] font-normal uppercase tracking-widest ${
                      active
                        ? "text-background/60"
                        : inactivePremium
                          ? "text-brand-600/80"
                          : "text-foreground/50"
                    }`}
                  >
                    from £{t.from}
                  </span>
                  {inactivePremium && (
                    <span
                      data-testid="pricing-premium-new-badge"
                      className="absolute -top-2 -right-2 inline-flex items-center gap-0.5 bg-brand text-white text-[9px] tracking-widest uppercase font-bold px-2 py-0.5 rounded-full shadow-md"
                    >
                      <Sparkles className="h-2.5 w-2.5" /> New
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {!isPremiumTier && (
            <button
              onClick={() => setTier("premium")}
              data-testid="pricing-premium-callout"
              className="group inline-flex items-center gap-2 text-xs md:text-sm font-medium text-foreground/70 hover:text-brand transition"
            >
              <span className="inline-flex h-2 w-2 rounded-full bg-brand brand-pulse" />
              Includes a 1-on-1 career consultation call
              <span className="group-hover:translate-x-1 transition">→</span>
            </button>
          )}
        </div>

        {/* Cards */}
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {visible.map((p) => (
            <article
              key={p.id}
              data-testid={`pricing-card-${p.id}`}
              className={`relative rounded-3xl p-8 md:p-10 border transition ${
                p.popular
                  ? "border-brand-400 bg-foreground text-background shadow-2xl shadow-brand-400/30 md:scale-[1.02]"
                  : "border-border bg-card text-foreground hover:border-foreground/30"
              }`}
            >
              {p.popular && (
                <span
                  data-testid="pricing-popular-badge"
                  className="absolute -top-3 left-8 inline-flex items-center gap-1.5 bg-brand text-white text-[11px] tracking-widest uppercase font-bold px-3 py-1.5 rounded-full"
                >
                  <Star className="h-3 w-3 fill-current" />{" "}
                  {isPremiumTier ? "Flagship" : "Most Popular"}
                </span>
              )}
              {isPremiumTier && (
                <span
                  className="absolute -top-3 right-8 inline-flex items-center gap-1.5 bg-background text-foreground text-[11px] tracking-widest uppercase font-bold px-3 py-1.5 rounded-full border border-border"
                  data-testid="pricing-premium-badge"
                >
                  <Crown className="h-3 w-3" /> Premium
                </span>
              )}
              <p
                className={`text-xs uppercase tracking-[0.22em] font-bold ${
                  p.popular ? "text-brand-300" : "text-muted-foreground"
                }`}
              >
                {p.tag}
              </p>
              <h3 className="mt-3 font-serif text-3xl md:text-4xl font-bold leading-tight">
                {p.name}
              </h3>
              {p.subtitle && (
                <p
                  className={`mt-1 text-sm font-medium ${
                    p.popular ? "text-background/70" : "text-foreground/60"
                  }`}
                >
                  {p.subtitle}
                </p>
              )}
              <p
                className={`mt-3 text-sm ${
                  p.popular ? "text-background/70" : "text-foreground/70"
                }`}
              >
                {p.description}
              </p>
              <div className="mt-7 flex items-baseline gap-2">
                <span className="font-serif text-6xl font-bold">£{p.price}</span>
                <span
                  className={`text-sm ${
                    p.popular ? "text-background/60" : "text-muted-foreground"
                  }`}
                >
                  one-time
                </span>
              </div>
              <ul className="mt-7 space-y-3">
                {p.features.map((f, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-sm leading-relaxed"
                  >
                    <span
                      className={`mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full ${
                        p.popular ? "bg-brand text-white" : "bg-brand/15 text-brand"
                      }`}
                    >
                      <Check className="h-3 w-3" />
                    </span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid={`pricing-buy-${p.id}`}
                onClick={() => navigate(`/checkout/${p.id}`)}
                className={`mt-9 w-full rounded-full py-3.5 text-sm font-semibold transition ${
                  p.popular
                    ? "bg-brand text-white hover:bg-brand-500"
                    : "btn-primary"
                }`}
              >
                Buy Now — £{p.price}
              </button>
              <p
                className={`mt-3 text-center text-[11px] ${
                  p.popular ? "text-background/60" : "text-muted-foreground"
                }`}
              >
                Secure payment via PayPal · 60-Day Interview Guarantee
              </p>
            </article>
          ))}
        </div>

        {/* Subtle hint to explore the other tier */}
        <p
          data-testid="pricing-switch-hint"
          className="mt-10 text-center text-sm text-muted-foreground"
        >
          {isPremiumTier ? (
            <>
              Looking for an entry-level option?{" "}
              <button
                onClick={() => setTier("standard")}
                data-testid="pricing-switch-to-standard"
                className="text-brand font-semibold hover:underline"
              >
                View Standard packages →
              </button>
            </>
          ) : (
            <>
              Need executive positioning + a 1-on-1 strategy call?{" "}
              <button
                onClick={() => setTier("premium")}
                data-testid="pricing-switch-to-premium"
                className="text-brand font-semibold hover:underline"
              >
                View Premium packages →
              </button>
            </>
          )}
        </p>
      </div>
    </section>
  );
}
